import React, { lazy, Suspense, useEffect, useRef } from 'react'
import { Redirect, Route, Switch, useLocation } from 'wouter'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Toaster } from '@/components/ui/Toaster'
import { gtagService } from '@/services/gtagService'
import { HomePage } from '@/pages/public/HomePage'
import { useSessionStore } from '@/store/sessionStore'
import { useAuthStore } from '@/store/authStore'
import { useSessionEndOnNavigate } from '@/hooks/useSessionEndOnNavigate'

type LazyComponent = React.LazyExoticComponent<React.ComponentType>

interface RouteConfig {
  path: string
  component: LazyComponent | React.ComponentType | typeof HomePage
  nest?: boolean
}

const lazyLoad = (importFn: () => Promise<{ default: React.ComponentType }>) =>
  lazy(() => importFn())

const PageLoading = () => (
  <div className="min-h-screen flex items-center justify-center">Yükleniyor...</div>
)

const pages = {
  welcome: lazyLoad(() => import('./pages/auth/WelcomePage')
    .then((m => ({ default: m.WelcomePage })))),
  forgotPassword: lazyLoad(() => import('./pages/auth/ForgotPasswordPage')
    .then((m => ({ default: m.ForgotPasswordPage })))),
  resetPassword: lazyLoad(() => import('./pages/auth/ResetPasswordPage')
    .then((m => ({ default: m.ResetPasswordPage })))),
  courses: lazyLoad(() => import('./pages/public/CoursesPage')
    .then((m => ({ default: m.CoursesPage })))),
  courseSearch: lazyLoad(() => import('./pages/public/CourseSearchPage').then((m => ({ default: m.CourseSearchPage })))),
  courseDetail: lazyLoad(() => import('./pages/public/CourseDetailPage').then((m => ({ default: m.CourseDetailPage })))),
  blog: lazyLoad(() => import('./pages/public/BlogPage').then((m => ({ default: m.BlogPage})))),
  blogDetail: lazyLoad(() => import('./pages/public/BlogDetailPage').then((m => ({ default: m.BlogDetailPage })))),
  about: lazyLoad(() => import('./pages/public/AboutPage').then((m => ({ default: m.AboutPage })))),
  contact: lazyLoad(() => import('./pages/public/ContactPage').then((m => ({ default: m.ContactPage })))),
  terms: lazyLoad(() => import('./pages/public/TermsPage').then((m => ({ default: m.TermsPage })))),
  privacy: lazyLoad(() => import('./pages/public/PrivacyPage').then((m => ({ default: m.PrivacyPage })))),
  music: lazyLoad(() => import('./pages/public/MusicPage').then((m => ({ default: m.MusicPage })))),
  quizder: lazyLoad(() => import('./pages/public/QuizderPage').then((m => ({ default: m.QuizderPage })))),
  iyilestirmeler: lazyLoad(() => import('./pages/public/IyilestirmelerPage').then((m => ({ default: m.IyilestirmelerPage })))),
  publicProfile: lazyLoad(() => import('./pages/PublicProfilePage').then((m => ({ default: m.PublicProfilePage }))))
} as const

const routeGroups = {
  student: lazyLoad(() => import('./routes/StudentRoutes').then((m => ({ default: m.StudentRoutes })))),
  parent: lazyLoad(() => import('./routes/ParentRoutes').then((m => ({ default: m.ParentRoutes })))),
  admin: lazyLoad(() => import('./routes/AdminRoutes').then((m => ({ default: m.AdminRoutes })))),
  payment: lazyLoad(() => import('./routes/PaymentRoutes').then((m => ({ default: m.PaymentRoutes }))))
} as const

const routes: RouteConfig[] = [
  { path: '/', component: HomePage },
  { path: '/welcome', component: pages.welcome },
  { path: '/forgot-password', component: pages.forgotPassword },
  { path: '/reset-password', component: pages.resetPassword },
  { path: '/kurslar', component: pages.courses },
  { path: '/kurslar/search', component: pages.courseSearch },
  { path: '/kurslar/:id', component: pages.courseDetail },
  { path: '/blog', component: pages.blog },
  { path: '/blog/:slug', component: pages.blogDetail },
  { path: '/about', component: pages.about },
  { path: '/contact', component: pages.contact },
  { path: '/terms', component: pages.terms },
  { path: '/privacy', component: pages.privacy },
  { path: '/music', component: pages.music },
  { path: '/quizder', component: pages.quizder },
  { path: '/iyilestirmeler', component: pages.iyilestirmeler },
  { path: '/paketler', component: () => <Redirect to="/kurslar" /> },
  { path: '/u/:username', component: pages.publicProfile },
  { path: '/student', component: routeGroups.student },
  { path: '/student/*', component: routeGroups.student },
  { path: '/rooms', component: routeGroups.student },
  { path: '/rooms/*', component: routeGroups.student },
  { path: '/feed', component: routeGroups.student },
  { path: '/arkadaslar', component: routeGroups.student },
  { path: '/parent', component: routeGroups.parent },
  { path: '/parent/*', component: routeGroups.parent },
  { path: '/admin', component: routeGroups.admin },
  { path: '/admin/*', component: routeGroups.admin },
  { path: '/payment/*', component: routeGroups.payment }
]

export function App() {
  const initializeFromPersisted = useSessionStore((state) => state.initializeFromPersisted)
  const user = useAuthStore((state) => state.user)
  const [location] = useLocation()
  const prevLocation = useRef<string | null>(null)

  useEffect(() => {
    initializeFromPersisted().catch(console.error)
  }, [initializeFromPersisted])

  useEffect(() => {
    if (prevLocation.current === null || prevLocation.current === location) {
      prevLocation.current = location
      return
    }
    prevLocation.current = location
    gtagService.trackPageView(document.title, window.location.href)
  }, [location])

  useSessionEndOnNavigate({
    enabled: !!user?.id,
    studyRoutes: ['/student/ders/', '/student/quiz/', '/student/egzersiz/']
  })

  return (
    <ErrorBoundary>
      <Switch>
        {routes.map(({ path, component: Component, nest }) => (
          <Route key={path} path={path} nest={nest}>
            {Component === HomePage ? (
              <HomePage />
            ) : (
              <Suspense fallback={<PageLoading />}>
                <Component />
              </Suspense>
            )}
          </Route>
        ))}
        <Route>
          <Suspense fallback={<PageLoading />}>
            <pages.blogDetail />
          </Suspense>
        </Route>
      </Switch>
      <Toaster />
    </ErrorBoundary>
  )
}
